import type { Area } from 'react-easy-crop';
import type { EmbeddedCourse, ExternalResource } from '@/models/resources';

export interface PathResponse {
  learning_path: LearningPath;
}

export type LearningPath = {
  id: string;
  contents: Content[];
  description: string;
  header_image_url?: string; // show
  header_image?: { blob: string; url: string } | null; // edit
  hidden: boolean;
  title: string;
  draft_version: boolean;
  has_published_version: boolean;
  global: boolean;
};

export type Version = {
  id: string;
  drafted_at: string | null;
  published_at: string | null;
  contents: Content[];
};

export type Content = TextContent | ResourceGroupContent;

export type TextContent = {
  id: string;
  position: number;
  type: ResourceTypes.Text;
  content: {
    body: string;
  };
};

export type ResourceGroupContent = {
  id: string;
  position: number;
  type: ResourceTypes.ResourceGroup;
  content: {
    resource_groups: ResourceGroup[];
  };
};

export enum ResourceTypes {
  Text = 'text',
  ResourceGroup = 'resource_group',
}

export type ResourceGroup =
  | { course: EmbeddedCourse; external_link?: never }
  | { external_link: ExternalResource; course?: never };

export type PathsMetadata = {
  current_page: number;
  total_pages: number;
  items_per_page: number;
  next_page: number | null;
  previous_page: number | null;
  first_page: boolean;
  last_page: boolean;
};

export type LearningPathItem = {
  id: string;
  title: string;
  description: string | null;
  company_id: string | null;
  path_type: string;
  header_image_url: string;
  hidden: boolean;
  global: boolean;
  company?: {
    company_name?: string;
  };
  resources_count: number;
  versions: {
    id: string;
    drafted_at: string | null;
    published_at: string | null;
  }[];
  position: number;
};

export interface LearningPathsResponse {
  metadata: PathsMetadata;
  learning_paths: LearningPathItem[];
  paths_count?: {
    company: number;
    global: number;
    total: number;
  };
}

export interface ManageLearningPathsResponse {
  learning_paths: {
    global_paths: LearningPathItem[];
    company_paths: LearningPathItem[];
  };
}

export interface CreatePathInput {
  learning_path: {
    title: string;
    description: string;
    path_type: PathType;
    external_company_id: number;
  };
}

export interface CreatePathResponse {
  learning_path: {
    id: string;
    title: string;
    description: string;
    hidden: boolean;
    header_image_url: string | null;
  };
}

export interface HeaderImage {
  blob: string;
  cropArea: Area;
  previewUrl?: string;
}

export interface EditPathInput {
  id: string;
  title: string;
  description: string;
  header_image?: HeaderImage;
  has_image_changed?: boolean;
  learning_path: {
    contents: Content[];
  };
}

export enum PathType {
  Company = 'company',
  Global = 'global',
}
